import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { generateFileKey, encryptFileBytes } from '@/lib/file-encryption';

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const ALLOWED_AUDIO_TYPES = ['audio/webm', 'audio/ogg', 'audio/mp4', 'audio/mpeg', 'audio/wav'];
const ALLOWED_DOC_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
];

const ALL_ALLOWED_TYPES = [
  ...ALLOWED_IMAGE_TYPES,
  ...ALLOWED_VIDEO_TYPES,
  ...ALLOWED_AUDIO_TYPES,
  ...ALLOWED_DOC_TYPES,
];

export type MediaCategory = 'image' | 'video' | 'audio' | 'voice_note' | 'file';

interface UploadResult {
  url: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  thumbnailUrl?: string;
  category: MediaCategory;
  /** Base64 AES-256-GCM key for this file (Signal-grade per-file encryption) */
  fileKey?: string;
}

export function useMediaUpload() {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const getCategory = (mimeType: string): MediaCategory => {
    if (ALLOWED_IMAGE_TYPES.includes(mimeType)) return 'image';
    if (ALLOWED_VIDEO_TYPES.includes(mimeType)) return 'video';
    if (ALLOWED_AUDIO_TYPES.includes(mimeType)) return 'audio';
    return 'file';
  };

  const generateThumbnail = async (file: File): Promise<string | undefined> => {
    if (!file.type.startsWith('image/')) return undefined;

    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX = 200;
        let w = img.width;
        let h = img.height;
        if (w > h) {
          h = (h / w) * MAX;
          w = MAX;
        } else {
          w = (w / h) * MAX;
          h = MAX;
        }
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL('image/jpeg', 0.6));
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(undefined);
      };
      img.src = url;
    });
  };

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB.`;
    }
    if (!ALL_ALLOWED_TYPES.includes(file.type)) {
      return 'File type not supported.';
    }
    return null;
  };

  const upload = useCallback(async (
    file: File,
    roomId: string,
    category?: MediaCategory,
  ): Promise<UploadResult | null> => {
    setError(null);

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return null;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // 1. Generate per-file encryption key
      const fileKey = await generateFileKey();
      setUploadProgress(10);

      // 2. Read file bytes and encrypt them client-side
      const plainBytes = await file.arrayBuffer();
      const encryptedBytes = await encryptFileBytes(plainBytes, fileKey);
      setUploadProgress(30);

      // 3. Upload encrypted blob (opaque - server sees random bytes)
      const fileId = crypto.randomUUID();
      const storagePath = `${roomId}/${fileId}.enc`;

      const encryptedBlob = new Blob([encryptedBytes], { type: 'application/octet-stream' });

      const { error: uploadError } = await supabase.storage
        .from('chat-media')
        .upload(storagePath, encryptedBlob, {
          contentType: 'application/octet-stream', // No real type leaks
          upsert: false,
        });

      if (uploadError) {
        throw new Error(uploadError.message);
      }

      setUploadProgress(80);

      // Generate thumbnail for images (kept local, will be encrypted in message JSON)
      const thumbnailUrl = await generateThumbnail(file);

      setUploadProgress(100);

      return {
        url: storagePath,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        thumbnailUrl,
        category: category || getCategory(file.type),
        fileKey,
      };
    } catch (err: any) {
      setError(err.message || 'Upload failed');
      return null;
    } finally {
      setIsUploading(false);
    }
  }, []);

  const uploadVoiceNote = useCallback(async (
    blob: Blob,
    roomId: string,
    durationSeconds: number,
  ): Promise<UploadResult | null> => {
    const file = new File([blob], `voice-${Date.now()}.webm`, { type: 'audio/webm' });
    return upload(file, roomId, 'voice_note');
  }, [upload]);

  return {
    upload,
    uploadVoiceNote,
    isUploading,
    uploadProgress,
    error,
  };
}

/**
 * Resolve a storage path (or legacy full URL) into a fresh signed URL.
 * Call this when rendering messages to ensure media links are always valid.
 */
export async function getMediaUrl(fileUrl: string): Promise<string> {
  // Legacy: if it's already a full URL, return as-is
  if (fileUrl.startsWith('http')) return fileUrl;

  // It's a storage path - generate a fresh 1-hour signed URL
  const { data } = await supabase.storage
    .from('chat-media')
    .createSignedUrl(fileUrl, 3600);

  return data?.signedUrl || fileUrl;
}
